import { useEffect, useRef, useState, useCallback } from 'react';

interface UseKeyboardNavigationOptions<T = unknown> {
  items: T[];
  onSelect?: (item: T, index: number) => void;
  onEscape?: () => void;
  isActive?: boolean;
  initialIndex?: number;
  wrapAround?: boolean;
}

export function useKeyboardNavigation<T = unknown>({
  items,
  onSelect,
  onEscape,
  isActive = true,
  initialIndex = -1,
  wrapAround = true,
}: UseKeyboardNavigationOptions<T>) {
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  // Reset refs array when items change
  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, items.length);
  }, [items.length]);

  // Focus management
  useEffect(() => {
    if (selectedIndex >= 0 && selectedIndex < items.length) {
      itemRefs.current[selectedIndex]?.focus();
    }
  }, [selectedIndex, items.length]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isActive || items.length === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prevIndex) => {
            if (prevIndex < items.length - 1) {
              return prevIndex + 1;
            }
            return wrapAround ? 0 : prevIndex;
          });
          break;

        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prevIndex) => {
            if (prevIndex > 0) {
              return prevIndex - 1;
            } else if (prevIndex === -1) {
              return items.length - 1;
            }
            return wrapAround ? items.length - 1 : prevIndex;
          });
          break;

        case 'Enter':
        case ' ':
          event.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < items.length && onSelect) {
            onSelect(items[selectedIndex], selectedIndex);
          }
          break;

        case 'Escape':
          event.preventDefault();
          if (onEscape) {
            onEscape();
          }
          break;

        default:
          break;
      }
    },
    [isActive, items, selectedIndex, onSelect, onEscape, wrapAround],
  );

  useEffect(() => {
    if (isActive) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleKeyDown, isActive]);

  const setItemRef = useCallback((index: number) => {
    return (el: HTMLElement | null) => {
      itemRefs.current[index] = el;
    };
  }, []);

  const handleItemHover = useCallback((index: number) => {
    setSelectedIndex(index);
  }, []);

  const handleItemClick = useCallback(
    (index: number) => {
      if (onSelect && items[index]) {
        onSelect(items[index], index);
      }
    },
    [items, onSelect],
  );

  return {
    selectedIndex,
    setSelectedIndex,
    setItemRef,
    handleItemHover,
    handleItemClick,
  };
}
